import { expValEquals } from '@confluence/feature-experiments';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import {
	captureLocalStorageQuotaSnapshot,
	type LocalStorageQuotaSnapshot,
} from './local-storage-quota-snapshot.ts';

// Document-scoped state: deduplication and buffering never write browser storage.
let ready = false;
let reporting = false;
let pendingWarning = false;
let warningReported = false;
let snapshot: LocalStorageQuotaSnapshot | undefined;

function incrementWarningCounter(): void {
	try {
		getMonitoringClient().incrementCounter('localStorageQuotaWarningShown', []);
	} catch {
		// Telemetry is best effort and must not interrupt quota handling or flag recovery.
	}
}

/** Flush only after the monitoring client's tenant/product context has been initialized. */
export function markLocalStorageQuotaReportingReady(): void {
	if (ready || reporting) return;
	ready = true;
	const warning = pendingWarning;
	pendingWarning = false;
	reporting = true;
	try {
		if (
			warning &&
			expValEquals('confluence_frontend_local_storage_quota_reporting', 'isEnabled', true)
		) {
			incrementWarningCounter();
		}
	} catch {
		// Experiment evaluation must not disrupt startup or storage recovery.
	} finally {
		reporting = false;
	}
}

/** Capture the first failure for the warning's diagnostics, without emitting a failure counter. */
export function captureLocalStorageQuotaFailureSnapshot(
	key: string,
	attemptedValueChars?: number,
): void {
	if (reporting || snapshot || warningReported) return;
	// Protect experiment evaluation too: exposure logging can invoke storage writers.
	reporting = true;
	try {
		if (expValEquals('confluence_frontend_local_storage_quota_reporting', 'isEnabled', true)) {
			// Capture the first failure before the warning offers to clear storage.
			snapshot = captureLocalStorageQuotaSnapshot(key, attemptedValueChars);
		}
	} catch {
		// Experiment evaluation must not replace the original storage error.
	} finally {
		reporting = false;
	}
}

/** Count once per document and return bounded attributes for the subscriber's shown UI event. */
export function reportLocalStorageQuotaWarningShown(): LocalStorageQuotaSnapshot | undefined {
	if (reporting || warningReported) return;
	reporting = true;
	try {
		if (expValEquals('confluence_frontend_local_storage_quota_reporting', 'isEnabled', true)) {
			warningReported = true;
			if (ready) {
				incrementWarningCounter();
			} else {
				pendingWarning = true;
			}
			// Also support quota events dispatched outside the patched setItem path.
			const diagnostic = snapshot ?? captureLocalStorageQuotaSnapshot();
			snapshot = undefined;
			return diagnostic;
		}
	} catch {
		// Experiment evaluation must not prevent the warning's Refresh action from working.
	} finally {
		reporting = false;
	}
}
